/** Everything this app remembers across a login, and the one way to forget it.
 *
 *  A new login must start on a blank workspace. Landing at `/` was treated as
 *  the whole fix and it is not — that fixes the route, while what makes a
 *  workspace look USED lives in browser storage nothing ever cleared:
 *
 *    tds.lastRun.<periodId>   puts `?run=32` back on any surface opened without one
 *    tds.openedSides          which 26AS / books sides "this session" loaded
 *
 *  Both are sessionStorage, scoped to the TAB rather than to the login, so
 *  logging out and back in without closing the tab kept them. You landed on `/`
 *  as designed, opened a period, and a previous session's run and files were
 *  already on screen.
 *
 *  Matched by PREFIX, not by a list of names. A list is a second place to
 *  remember, and the next module to persist something would have to find this
 *  file first — the same reason the run and the sides had to stop using two
 *  different lifetimes.
 *
 *  This resets a CLAIM, never data. Periods, runs and uploads all stay in the
 *  database and can be opened again; what is dropped is the assertion about
 *  what this session has already looked at.
 *
 *  **Called from `lib/session-end.ts`, and from nowhere else** (ADR-0012). It
 *  had no production caller at all until 2026-09-10: this module, its tests and
 *  three comments claiming it ran were the whole of it. If a second call site
 *  ever appears, ask why — "the session ended" should have exactly one home.
 */
export const SESSION_PREFIX = 'tds.'

/** Drop every key in our namespace. Returns what was removed, so a caller can
 *  be tested on the effect rather than on having made the call. */
export function clearSessionState(store: Storage): string[] {
  let doomed: string[]
  try {
    // Collected BEFORE anything is deleted. `removeItem` while walking by index
    // shifts every later key down one, which is how a straightforward loop
    // leaves half the namespace behind — and half-cleared is worse than
    // uncleared, because the screen then looks fresh while still restoring a
    // run.
    doomed = Array.from({ length: store.length }, (_, i) => store.key(i))
      .filter((k): k is string => k != null && k.startsWith(SESSION_PREFIX))
  } catch {
    // Safari private mode and locked-down enterprise profiles throw on access.
    // A login must not fail because the browser will not talk about storage.
    return []
  }
  for (const k of doomed) {
    try {
      store.removeItem(k)
    } catch {
      /* ignore — a key we cannot drop is not worth failing a login over */
    }
  }
  return doomed
}
